import React, { useContext, useMemo } from 'react';
import { connect } from 'react-redux';
import { PublishedWithChanges, Unpublished, ChangeCircle, Warning } from '@mui/icons-material';
import { List, ListItem } from '@mui/material';
import StagingDataTable from './StagingDataTable';
import ColumnDefinition from '../constants/ColumnDefinition';
import TableAction from '../constants/TableAction';
import { demoteStaging, updateStagingPoints } from '../../../redux/actions/staging';
import { dateTime, dateToISO8601 } from '../../../utils/parsing';
import StatusBadge from '../constants/StatusBadge';
import FilterContext from '../contexts/FilterContext';

const PointsDataTable = ({
  buildingId,
  points,
  selectedTopics,
  onItemSelected,

  units,
  pointTypes,

  updateStagingPointsUncaught,
  demoteStagingUncaught,
}) => {
  const { filters } = useContext(FilterContext);
  const filteredPoints = useMemo(
    () => Array.from(points.values().filter((point) => filters.every((f) => f.filter(point)))),
    [points, filters]
  );

  const [loading, setLoading] = React.useState(false);

  function extractEquipment(point, extractFn) {
    return (point.equip ?? []).map((equip) => extractFn(equip));
  }

  function buildValidationTooltip(point) {
    if (point?.validation?.length === 0) return null;
    return (
      <>
        This point contains validation errors:
        <List sx={{ listStyleType: 'disc' }}>
          {point?.validation?.map((v) => (
            <ListItem key={v.field + v.error_class} sx={{ display: 'list-item', padding: 0 }}>
              {v.field} - {v.message}
            </ListItem>
          ))}
        </List>
      </>
    );
  }
  const columns = [
    new ColumnDefinition({
      id: 'point_badges',
      label: 'Status',
      type: 'badges',
      sortable: true,
      action: 'unlink_point',
      sortFunction: (pointA, pointB) => {
        const valueA = pointA.validation?.length || 0;
        const valueB = pointB.validation?.length || 0;
        return valueB - valueA;
      },
      render: (point) => {
        const statuses = [];
        if (!point.promoted_point_id)
          statuses.push(
            new StatusBadge({
              id: 'published',
              icon: Unpublished,
              message: 'This point is not published.',
            })
          );
        else
          statuses.push(
            new StatusBadge({
              id: 'unpublished',
              icon: PublishedWithChanges,
              color: 'success',
              message: `This point was last published ${dateTime(point.last_promoted)}.`,
            })
          );
        if (point?.validation?.length > 0)
          statuses.push(
            new StatusBadge({
              id: 'publish_error',
              icon: Warning,
              color: 'error',
              message: buildValidationTooltip(point),
            })
          );
        if (point.promoted_point_id && point.last_promoted < point.modified)
          statuses.push(
            new StatusBadge({
              id: 'changes',
              icon: ChangeCircle,
              color: 'warning',
              message: `This point has unpublished changes.`,
            })
          );
        return statuses;
      },
    }),
    new ColumnDefinition({
      id: 'point_type',
      label: 'Point Type',
      type: 'button',
      sortable: true,
      action: 'change_point_type',
      render: (point) => pointTypes[point.point_type_id]?.name,
    }),
    new ColumnDefinition({
      id: 'confidence',
      label: 'Point Type Confidence',
      type: 'linear',
      sortable: true,
      hideable: true,
      sortFunction: (pointA, pointB) => {
        const valueA = pointA.confidences.point_type_id || 0;
        const valueB = pointB.confidences.point_type_id || 0;
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      },
      render: (point) => point.confidences.point_type_id,
    }),
    new ColumnDefinition({
      id: 'point_name',
      label: 'Point Name',
      type: 'text',
      hideable: true,
      render: (point) => point.properties?.objectName,
    }),
    new ColumnDefinition({
      id: 'point_description',
      label: 'Description',
      type: 'text',
      hideable: true,
      render: (point) => point.properties?.description,
    }),
    new ColumnDefinition({
      id: 'point_value',
      label: 'Present Value',
      type: 'text',
      hideable: true,
      render: (point) => point.properties?.presentValue,
    }),
    new ColumnDefinition({
      id: 'units',
      label: 'Units',
      type: 'button',
      action: 'change_unit_type',
      render: (point) => units[point.raw_unit_id]?.name_abbr,
    }),
    new ColumnDefinition({
      id: 'equip_name',
      label: 'Equip names',
      type: 'text',
      render: (point) => extractEquipment(point, (e) => e.name).join(', '),
    }),
    new ColumnDefinition({
      id: 'object_identifier',
      label: 'Object ID',
      type: 'text',
      hideable: true,
      defaultHidden: true,
      render: (point) => (point.properties?.objectIdentifier ?? []).join(', '),
    }),
    new ColumnDefinition({
      id: 'device_identifier',
      label: 'Device ID',
      type: 'text',
      hideable: true,
      defaultHidden: true,
      render: (point) => point.device_id ?? point.data['e.device_id'],
    }),
    new ColumnDefinition({
      id: 'first_discovered_time',
      label: 'First Discovered Time',
      type: 'time',
      hideable: true,
      sortable: true,
      render: (point) => dateToISO8601(new Date(point.created)),
    }),
    new ColumnDefinition({
      id: 'last_discovered_time',
      label: 'Last Discovered Time',
      type: 'time',
      hideable: true,
      sortable: true,
      defaultHidden: true,
      render: (point) => dateToISO8601(new Date(point.last_discovery)),
    }),
    new ColumnDefinition({
      id: 'alarm_value',
      label: 'Alarm Value',
      type: 'text',
      hideable: true,
      defaultHidden: true,
      render: (point) => point.properties?.alarmValue,
    }),
    new ColumnDefinition({
      id: 'active_text',
      label: 'Active/Inactive',
      type: 'text',
      hideable: true,
      defaultHidden: true,
      render: (point) =>
        [point.properties?.activeText, point.properties?.inactiveText].filter((x) => x).join(' / '),
    }),
  ];

  const update = (data, newPointValues) => {
    // Only need to update equip_ids if we're changing assignments, which we're not doing from this view
    const updateRowValues = data.map((datum) => ({ ...newPointValues, topic: datum.topic }));
    setLoading(true);
    // Currently skip equipment isn't actually actualized or streamlined, so if we're trying to skip something, start with a SKIP equip to ensure it exists
    return updateStagingPointsUncaught(updateRowValues).then(() => {
      setLoading(false);
    });
  };

  const unlinkPoints = (removedPoints) => {
    const pointIds = removedPoints.map((p) => p.promoted_point_id).filter(Boolean);
    let demote = Promise.resolve();
    if (pointIds.length > 0) {
      demote = demoteStagingUncaught({
        equipment_ids: [],
        point_ids: pointIds,
        point_equipment_relationships: removedPoints
          .filter(({ promoted_point_id }) => Boolean(promoted_point_id))
          .flatMap((point) =>
            point.equip
              .filter((equip) => equip.promoted_equipment_id)
              .map((equip) => ({
                equipment_id: equip.promoted_equipment_id,
                point_id: point.promoted_point_id,
              }))
          ),
      });
    }
    return demote.then(() =>
      updateStagingPointsUncaught(removedPoints.map((p) => ({ topic: p.topic, equip_names: [] })))
    );
  };

  const actions = [
    new TableAction({
      id: 'change_point_type',
      label: 'Change Point Type',
      doSave: update,
    }),
    new TableAction({
      id: 'change_unit_type',
      label: 'Change Unit Type',
      doSave: update,
    }),
    new TableAction({
      id: 'unlink_point',
      label: 'Remove from Equip',
      doSave: unlinkPoints,
      disabled: (selectedPoints) =>
        selectedPoints.filter((p) => (p.equip ?? []).length > 0).length <= 0,
    }),
  ];

  return (
    <StagingDataTable
      title="Points"
      rowIdKey="topic"
      actions={actions}
      columns={columns}
      allData={points}
      filteredData={filteredPoints}
      selectedIds={selectedTopics}
      onItemSelected={onItemSelected}
      loading={loading}
      buildingId={buildingId}
    />
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  buildingId,
  units: state.entities.units.byId,
  pointTypes: state.entities.pointTypes.byId,
});

const mapDispatchToProps = (dispatch, { buildingId }) => ({
  updateStagingPointsUncaught: (equipId, data) =>
    dispatch(updateStagingPoints(buildingId, equipId, data)),
  demoteStagingUncaught: (data) => dispatch(demoteStaging(buildingId, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PointsDataTable);
